import { useRef } from "react";
import { ArrowRight, Linkedin, MapPin, Sparkles, Star } from "lucide-react";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useTranslation } from "react-i18next";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import type { ProviderCardData } from "./ProviderCard";

/**
 * Editorial "Featured" rail: 1 hero card on the left + up to 2 stacked cards on the right.
 * Hides itself when no featured items exist. Falls back gracefully for 1 or 2 items.
 */
export function FeaturedSpotlight({ items }: { items: ProviderCardData[] }) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "en").startsWith("fr") ? "fr" : "en";
  const list = items.slice(0, 3);
  if (!list.length) return null;

  const hero = list[0];
  const rest = list.slice(1);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <SpotlightHero p={hero} lang={lang} />
      {rest.length > 0 && (
        <div className="flex flex-col gap-4">
          {rest.map((p) => (
            <SpotlightSecondary key={p.user_id} p={p} lang={lang} />
          ))}
          {rest.length === 1 && <div className="hidden flex-1 lg:block" />}
        </div>
      )}
    </div>
  );
}

function FeaturedRibbon({ lang }: { lang: "en" | "fr" }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-md">
      <Sparkles className="h-3.5 w-3.5" />
      {lang === "fr" ? "Sélectionné" : "Featured"}
    </span>
  );
}

function Media({ p, ratio }: { p: ProviderCardData; ratio: number }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const photo = p.photo_url || p.avatar_url || null;
  const initials = `${p.first_name?.[0] ?? ""}${p.last_name?.[0] ?? ""}`.toUpperCase() || "?";
  const fullName = `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim();
  return (
    <div
      className="relative h-full w-full overflow-hidden"
      onMouseEnter={() => videoRef.current?.play().catch(() => {})}
      onMouseLeave={() => {
        const v = videoRef.current;
        if (v) { v.pause(); v.currentTime = 0; }
      }}
    >
      <AspectRatio ratio={ratio}>
        {p.intro_video_url ? (
          <video
            ref={videoRef}
            src={p.intro_video_url}
            poster={photo ?? undefined}
            muted
            playsInline
            loop
            preload="metadata"
            className="h-full w-full object-cover transition-transform duration-700 ease-brand group-hover:scale-[1.04]"
          />
        ) : photo ? (
          <img
            src={photo}
            alt={fullName || "Provider"}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-brand group-hover:scale-[1.04]"
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center text-6xl font-extrabold text-white"
            style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent)))" }}
          >
            {initials}
          </div>
        )}
      </AspectRatio>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/30 to-transparent" />
    </div>
  );
}

function SpotlightHero({ p, lang }: { p: ProviderCardData; lang: "en" | "fr" }) {
  const fullName = `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim();
  const unitSuffix = p.price_unit === "month" ? (lang === "fr" ? "/mois" : "/mo") : "/h";
  const href = p.detail_href ?? `/services/coaching/${p.user_id}`;
  return (
    <Link
      to={href}
      className="group relative flex min-h-[420px] flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-md transition-all duration-base ease-brand hover:-translate-y-0.5 hover:shadow-xl"
    >
      <Media p={p} ratio={4 / 3} />
      <div className="absolute left-4 top-4 z-[2] flex flex-col items-start gap-2">
        <FeaturedRibbon lang={lang} />
      </div>
      {p.linkedin_url && (
        <a
          href={p.linkedin_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          aria-label={`LinkedIn profile of ${fullName || "provider"}`}
          className="absolute right-4 top-4 z-[2] inline-flex h-8 w-8 items-center justify-center rounded-full bg-background/95 text-primary shadow transition hover:bg-primary hover:text-primary-foreground"
        >
          <Linkedin className="h-4 w-4" />
        </a>
      )}
      <div className="absolute inset-x-0 bottom-0 z-[2] p-6 text-white">
        <h3 className="font-display text-2xl font-bold tracking-tight md:text-3xl">{fullName || "—"}</h3>
        {p.headline && (
          <p className="mt-1 line-clamp-2 max-w-xl text-sm text-white/90 md:text-base">{p.headline}</p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/85">
          {p.city && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" /> {p.city}
            </span>
          )}
          {p.rating_avg != null && (
            <span className="inline-flex items-center gap-1">
              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
              <span className="font-semibold text-white">{Number(p.rating_avg).toFixed(1)}</span>
              <span>({p.rating_count ?? 0})</span>
            </span>
          )}
        </div>
        {!!(p.expertise_areas ?? []).length && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {(p.expertise_areas ?? []).slice(0, 3).map((t) => (
              <span
                key={t}
                className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm"
              >
                {t}
              </span>
            ))}
          </div>
        )}
        <div className="mt-4 flex items-end justify-between">
          <div>
            {p.hourly_eur != null ? (
              <>
                <div className="text-[10px] font-medium uppercase tracking-wider text-white/70">
                  {lang === "fr" ? "À partir de" : "From"}
                </div>
                <div className="text-xl font-bold leading-tight text-white">
                  €{p.hourly_eur.toLocaleString("fr-FR")}
                  {p.price_eur_max != null && p.price_eur_max !== p.hourly_eur ? `–€${p.price_eur_max.toLocaleString("fr-FR")}` : ""}
                  {unitSuffix}
                </div>
              </>
            ) : (
              <span className="text-sm text-white/80">{lang === "fr" ? "Sur devis" : "On request"}</span>
            )}
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-bold text-foreground shadow transition group-hover:bg-primary group-hover:text-primary-foreground">
            {lang === "fr" ? "Voir le profil" : "View profile"}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function SpotlightSecondary({ p, lang }: { p: ProviderCardData; lang: "en" | "fr" }) {
  const fullName = `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim();
  const unitSuffix = p.price_unit === "month" ? (lang === "fr" ? "/mois" : "/mo") : "/h";
  const href = p.detail_href ?? `/services/coaching/${p.user_id}`;
  return (
    <Link
      to={href}
      className="group relative flex flex-1 min-h-[200px] gap-4 overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all duration-base ease-brand hover:-translate-y-0.5 hover:shadow-lg hover:border-primary/40"
    >
      <div className="relative w-2/5 shrink-0 overflow-hidden">
        <Media p={p} ratio={1} />
        <div className="absolute left-2 top-2 z-[2]">
          <FeaturedRibbon lang={lang} />
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-2 py-4 pr-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display text-lg font-bold leading-tight text-foreground">{fullName || "—"}</h3>
          {p.linkedin_url && (
            <a
              href={p.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              aria-label="LinkedIn"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-primary hover:bg-primary/10"
            >
              <Linkedin className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
        {p.headline && <p className="line-clamp-2 text-sm text-muted-foreground">{p.headline}</p>}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          {p.city && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {p.city}
            </span>
          )}
          {p.rating_avg != null && (
            <span className="inline-flex items-center gap-1">
              <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
              <span className="font-semibold text-foreground">{Number(p.rating_avg).toFixed(1)}</span>
            </span>
          )}
        </div>
        <div className="mt-auto flex items-end justify-between border-t border-border pt-2">
          {p.hourly_eur != null ? (
            <div className="text-sm font-bold text-foreground">
              €{p.hourly_eur.toLocaleString("fr-FR")}
              {p.price_eur_max != null && p.price_eur_max !== p.hourly_eur ? `–€${p.price_eur_max.toLocaleString("fr-FR")}` : ""}
              {unitSuffix}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">{lang === "fr" ? "Sur devis" : "On request"}</span>
          )}
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
            {lang === "fr" ? "Voir" : "View"}
            <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}